import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { StaffDisputeAttachment, StaffDisputeLine } from '../../services/staff-dispute.service';

@Component({
    selector: 'app-dispute-attachment-card',
    standalone: true,
    imports: [CommonModule, DatePipe, ButtonModule],
    template: `
        <div class="attachment-card">
            <h3 class="section-title">Attachments</h3>
            <div class="attachments-grid" *ngIf="attachments.length; else empty">
                <div class="attachment-tile" *ngFor="let a of attachments">
                    <div class="thumb-wrap">
                        <img
                            *ngIf="isImage(a) && previewUrl(a.id)"
                            [src]="previewUrl(a.id)!"
                            [alt]="a.originalFileName"
                            class="thumb"
                        />
                        <div *ngIf="isImage(a) && !previewUrl(a.id)" class="thumb placeholder">Loading…</div>
                        <div *ngIf="!isImage(a)" class="thumb placeholder"><i class="pi pi-file-pdf"></i></div>
                    </div>
                    <div class="attachment-info">
                        <strong class="file-name">{{ a.originalFileName }}</strong>
                        <span class="item-label" *ngIf="itemLabel(a)">{{ itemLabel(a) }}</span>
                        <span class="uploaded-by">Uploaded by Customer</span>
                        <span class="uploaded-at">{{ a.uploadedAt | date: 'd MMM y' }}</span>
                        <div class="attachment-actions">
                            <button pButton type="button" label="Open" size="small" severity="secondary" (click)="open.emit(a)"></button>
                            <button pButton type="button" label="Download" size="small" class="btn-primary-dispute" (click)="download.emit(a)"></button>
                        </div>
                    </div>
                </div>
            </div>
            <ng-template #empty>
                <p class="empty">No attachments.</p>
            </ng-template>
        </div>
    `,
    styles: [
        `
            .attachment-card {
                background: var(--dispute-card, var(--surface-card));
                border: 1px solid var(--dispute-border, var(--surface-border));
                border-radius: 12px;
                padding: 1rem 1.25rem;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
            }
            .section-title {
                margin: 0 0 0.85rem;
                font-size: 0.95rem;
                font-weight: 600;
            }
            .attachments-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                gap: 1rem;
            }
            .attachment-tile {
                background: var(--dispute-nested-bg, var(--surface-secondary));
                border: 1px solid var(--dispute-border, var(--surface-border));
                border-radius: 10px;
                overflow: hidden;
            }
            .thumb-wrap {
                background: var(--dispute-thumb-bg, var(--surface-tertiary));
                border-bottom: 1px solid var(--dispute-border, var(--surface-border));
            }
            .thumb {
                width: 100%;
                height: 140px;
                object-fit: contain;
                display: block;
            }
            .thumb.placeholder {
                height: 140px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: var(--dispute-muted, var(--text-color-secondary));
                font-size: 0.85rem;
            }
            .attachment-info {
                padding: 0.75rem;
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
            }
            .file-name {
                font-size: 0.85rem;
                word-break: break-word;
            }
            .item-label {
                font-size: 0.8rem;
                font-weight: 600;
                color: var(--dispute-text, var(--text-color));
            }
            .uploaded-by,
            .uploaded-at {
                font-size: 0.75rem;
                color: var(--dispute-muted, var(--text-color-secondary));
            }
            .attachment-actions {
                display: flex;
                gap: 0.5rem;
                margin-top: 0.5rem;
            }
            .empty {
                margin: 0;
                color: var(--dispute-muted, var(--text-color-secondary));
            }
        `
    ]
})
export class DisputeAttachmentCardComponent {
    @Input({ required: true }) attachments: StaffDisputeAttachment[] = [];
    @Input() lines: StaffDisputeLine[] = [];
    @Input() previewUrls: Map<number, string> = new Map();
    @Output() open = new EventEmitter<StaffDisputeAttachment>();
    @Output() download = new EventEmitter<StaffDisputeAttachment>();

    isImage(a: StaffDisputeAttachment): boolean {
        return (a.contentType || '').toLowerCase().startsWith('image/');
    }

    previewUrl(id: number): string | null {
        return this.previewUrls.get(id) || null;
    }

    itemLabel(a: StaffDisputeAttachment): string | null {
        const claimLineId = a.claimLineId;
        if (claimLineId == null) {
            return null;
        }
        const line = this.lines.find((l) => l.id === claimLineId);
        if (!line) {
            return null;
        }
        const name = line.itemName?.trim();
        if (name) {
            return name;
        }
        const num = line.itemNumber?.trim();
        return num ? `Item ${num}` : null;
    }
}
